import React, { useMemo } from 'react';
import { find, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { Box, Button, CardContent, Grid, List, ListItem, Paper, Typography } from '@availity/element';

import { Footer, BackupCodeDisplay, AvailityHeaderLogo } from '@/components';
import { TwoFAIcon00 } from '@/static';
import { useFRCallback } from '@/hooks';

// TODO: extract navigation options and backup codes from response
const TwoFAConfirmation = ({ ...props }) => {
  const { stepToRender, setCurrentStep, showBackupCodes } = props;

  const {
    callbacks,
    confirmationCallbackIndex,
    confirmationCallbackOptions = [],
    textOutputCallback,
  } = useFRCallback(stepToRender);

  const backupCodes = useMemo(() => {
    let output = [];
    if (textOutputCallback) {
      const textOutputArray = find(
        textOutputCallback.output,
        (outputObj) => outputObj?.name?.toLowerCase() === 'message'
      );

      // NOTE: if no codes present, the value will be 'default message'; in this case return an empty array.
      const unparsedOutput = textOutputArray?.value?.toLowerCase() !== 'default message' && textOutputArray?.value;

      output = unparsedOutput ? JSON.parse(unparsedOutput) : [];
    }

    return output;
  }, [textOutputCallback]);

  const hasNewDeviceButton = useMemo(
    () => confirmationCallbackOptions.indexOf('newDevice') >= 0,
    [confirmationCallbackOptions]
  );

  const handleNavigation = (selectedNavigation) => {
    // if backup codes render, the 'back button' serves as the 'add a new device' navigation option; else look for 'new device' instead.
    const newDeviceOption = showBackupCodes ? 'new device' : 'no';

    const nextStep = stepToRender;
    const newCallbacks = [...callbacks];

    if (selectedNavigation === 'newDevice') {
      const newDeviceIndex = findIndex(
        confirmationCallbackOptions,
        (button) => button?.toLowerCase() === newDeviceOption
      );

      newCallbacks[confirmationCallbackIndex].input[0] = {
        ...newCallbacks[confirmationCallbackIndex].input[0],
        value: newDeviceIndex,
      };

      // set the newCallbacks in the newStep
      Object.assign(nextStep.payload.callbacks, newCallbacks);
    }

    // pass in same step to go to the next step;
    setCurrentStep({ ...stepToRender });
  };

  return (
    <Grid
      id="2fa-confirmation-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        id="2fa-confirmation-page-content-grid"
        container
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        mt="auto"
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '750px', lg: '1000px' } }}
          >
            <CardContent>
              <Grid display="flex" flexDirection="row" flexWrap="wrap">
                <Grid size={{ sm: 12, md: 4 }} sx={{ marginBottom: { xs: 2, md: 0 }, paddingRight: 2 }}>
                  <Box textAlign="center" mb={2}>
                    <img
                      src={TwoFAIcon00}
                      alt="2FA Icon"
                      style={{
                        maxWidth: '45%',
                      }}
                    />
                  </Box>
                  <Typography variant="h5" component="h3" mb={2}>
                    Availity takes HIPAA seriously.
                  </Typography>
                  <Typography variant="subtitle1">
                    We are continually enhancing the service and security we provide our customers and their patients.
                  </Typography>
                </Grid>
                <Grid
                  display="flex"
                  flexDirection="column"
                  size={{ md: 8 }}
                  sx={{ borderLeft: { sm: 0, md: 1 }, borderColor: { md: '#D7DAE0' } }}
                >
                  <Box pl={2}>
                    <Typography variant="h1" mb={1}>
                      Confirmation
                    </Typography>
                    <Typography variant="h4" component="h2" mb={2}>
                      You&apos;re all set
                    </Typography>
                  </Box>
                  <Grid display="flex" flexDirection="column" flexGrow={1} pl={2}>
                    {showBackupCodes ? (
                      <Box pt={2} mb={4}>
                        <Typography variant="subtitle1" mb={3}>
                          If you don&apos;t have access to your device, use these one-time backup codes to access your
                          account.
                        </Typography>
                        <Typography variant="subtitle1" mb="2">
                          <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Keep these backup codes in a safe place only you know about.
                          </Box>
                        </Typography>
                        <Grid display="flex" flexWrap="wrap" my={4}>
                          <BackupCodeDisplay backupCodes={backupCodes} />
                        </Grid>
                        <Typography mb={4}>
                          <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Note:
                          </Box>{' '}
                          These codes expire after one use. Sharing backup codes may violate HIPAA regulations regarding
                          data privacy.
                        </Typography>
                      </Box>
                    ) : (
                      <Box pt={2} mb={4}>
                        <Typography variant="subtitle1">
                          To update your 2-Step Authentication methods or retrieve an updated list of backup codes:
                        </Typography>
                        <List
                          component="ol"
                          sx={{
                            listStyleType: 'decimal',
                            listStylePosition: 'outside',
                            paddingX: 2,
                          }}
                        >
                          <ListItem divider={false} sx={{ display: 'list-item' }}>
                            <Typography component="span" variant="subtitle1">
                              From the homepage, open{' '}
                              <Box component="span" sx={{ fontWeight: 'bold' }}>
                                My Account
                              </Box>
                              .
                            </Typography>
                          </ListItem>
                          <ListItem divider={false} sx={{ display: 'list-item' }}>
                            <Typography component="span" variant="subtitle1">
                              Click the{' '}
                              <Box component="span" sx={{ fontWeight: 'bold' }}>
                                Security
                              </Box>{' '}
                              tab.
                            </Typography>
                          </ListItem>
                          <ListItem divider={false} sx={{ display: 'list-item' }}>
                            <Typography component="span" variant="subtitle1">
                              Click{' '}
                              <Box component="span" sx={{ fontWeight: 'bold' }}>
                                Update 2-step authentication
                              </Box>
                              .
                            </Typography>
                          </ListItem>
                        </List>
                      </Box>
                    )}
                    <Box textAlign="right" mt="auto">
                      {hasNewDeviceButton && (
                        <Button color="secondary" onClick={() => handleNavigation('newDevice')} sx={{ marginLeft: 1 }}>
                          Add another device
                        </Button>
                      )}
                      <Button color="primary" sx={{ marginLeft: 1 }} onClick={() => handleNavigation()}>
                        Continue
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

TwoFAConfirmation.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  showBackupCodes: PropTypes.bool.isRequired,
};

export default TwoFAConfirmation;
